import React, {useCallback, useRef, useState, useEffect} from 'react';
import { FaArrowUp } from 'react-icons/fa';
import { EntryType, EntryModel, BucketData, EntryRef, TextData } from 'lib/models';
import { updateEntry, getEntry, addEntryRef, createEntry } from 'lib/api';
import EntryContentContainer from 'components/MoveableEntry/EntryContentContainer';
import EditableTitle from 'components/EditableTitle';
import Button, { ButtonVariant } from 'components/Button';
import BucketContentContainer from './BucketContentContainer';

interface BucketEntryProps {
  refId: string;
  model: EntryModel<BucketData>;
}

function BucketEntry({model}: BucketEntryProps) {
  const [inputValue, setInputValue] = useState<string|undefined>(undefined);
  const [entryRefsMap, setEntryRefsMap] = useState<{[key: string]: EntryRef}>({});
  const [entriesMap, setEntriesMap] = useState<{[key: string]: EntryModel}>({});
  const [isEmpty, setIsEmpty] = useState<boolean>(false);

  const contentContainerRef = useRef<HTMLDivElement|null>(null);
  const isInitialLoad = useRef(true);

  const saveTitle = useCallback(async (title: string = ""): Promise<void> => {
      const updatePatch: Partial<BucketData> = {name: title};
      await updateEntry(model.uuid, model.data.type, updatePatch);
  }, []);

  const handleSubmit = async () => {
    if (!inputValue) {
      return;
    }
    const initialData: Partial<TextData> = { text: inputValue };
    const entry = await createEntry(EntryType.TEXT, initialData);
    const entryRef = await addEntryRef(
      EntryType.BUCKET,
      model.uuid,
      entry.data.type,
      entry.uuid,
    );
    setEntryRefsMap({...entryRefsMap, [entryRef.refId]: entryRef });
    entriesMap[entry.uuid] = entry;
    setIsEmpty(false);
    setInputValue("");
  };

  const handleFormSubmit = (e: any) => {
    e.preventDefault();
    handleSubmit();
  };

  const handleKeyDown = (e: any) => {
    if (e.key == 'Enter' && e.ctrlKey) {
      handleFormSubmit(e);
    }
  };

  useEffect(() => {
    if (contentContainerRef.current) {
      contentContainerRef.current.scrollTop = contentContainerRef.current.scrollHeight;
    }
  }, [entryRefsMap]);

  useEffect(() => {
    const loadEntries = async () => {
      if(!isInitialLoad.current) return;
      isInitialLoad.current = false;

      const entries = await Promise.all(
        Object.values(model.data.entryRefs).map((x: EntryRef) => {
          return getEntry(x.entryUuid, x.entryType);
        })
      );
      setEntryRefsMap(model.data.entryRefs);

      const entriesMap = entries.reduce((dict, obj) => {
        dict[obj.uuid] = obj;
        return dict;
      }, {} as { [key: string]: EntryModel });
      setEntriesMap(entriesMap);
      setIsEmpty(Object.values(entriesMap).length === 0);
    };
    if (model.data) {
      loadEntries();
    }
  }, [model.data, model.data.entryRefs]);

  return (
    <div
      className={`flex flex-col space-y-2 p-2 bg-red-400 rounded-lg min-w-48 w-full h-full`}
    >
      <EditableTitle
        initialText={model.data.name || "Brain Dump"}
        onTitleChange={saveTitle}
      />
      <EntryContentContainer containerRef={contentContainerRef} className="space-y-2 h-fit flex-1">
        {!isEmpty ?
          (Object.values(entryRefsMap).map((ref, idx) => {
            return (!!entriesMap[ref.entryUuid] ? (
              <div key={`bucket:entry:${idx}`} className="pr-4">
                <BucketContentContainer entry={entriesMap[ref.entryUuid]} />
              </div>
            ): null);
          }
          )) : null}
      </EntryContentContainer>
      <div className="flex min-w-48 w-full items-center space-x-2">
        <form
          className="w-full overflow-x-hidden min-w-60"
          onSubmit={handleFormSubmit}
        >
          <textarea
            className="w-full focus:outline-none text-palette-1 placeholder-palette-2 text-palette-5 resize-none p-2 rounded-lg whitespace-pre-wrap break-words"
            rows={2}
            placeholder={"Add anything"}
            value={inputValue}
            onKeyDown={handleKeyDown}
            onChange={(event) => setInputValue(event.target.value)}
          />
        </form>
        <div>
          <Button
            variant={ButtonVariant.FlatNoFill}
            icon={<FaArrowUp/>}
            disabled={!inputValue}
            onClick={handleSubmit}
          />
        </div>
      </div>
    </div>
  );
}

export default BucketEntry;
